import React from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { LocaleProvider } from '../../i18n/LocaleContext';
import '../../styles/landing.css';
import '../../styles/landing-neon.css';

const SUPPORT_EMAIL = 'gather.community.org@gmail.com';

export const DeleteAccountPage = (): React.JSX.Element => {
  return (
    <LocaleProvider>
      <div className="landing landing--neon" id="top">
        <Header />
        <main>
          <section className="da-section da-hero">
            <div className="wrap da-wrap">
              <span className="eyebrow">Account management</span>
              <h1 className="da-title">
                Delete your <span className="grad-text">Gather account</span>
              </h1>
              <p className="da-lead">
                You can request deletion of your Gather account and all
                associated personal data at any time. There are two ways to do
                it: from inside the app, or by emailing us.
              </p>
              <div className="da-cta-row">
                <a className="btn btn-primary" href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20Gather%20account`}>
                  Email a deletion request
                </a>
                <a className="btn btn-outline" href="#in-app">
                  In-app instructions
                </a>
              </div>
            </div>
          </section>

          <section className="da-section da-section--tight">
            <div className="wrap da-wrap">
              <span className="eyebrow">What gets deleted</span>
              <h2 className="da-h2">Personal data we remove</h2>
              <ul className="da-list">
                <li>Your profile (name, email, photo, bio)</li>
                <li>Posts, comments, and chat messages you created</li>
                <li>Events you created and your RSVPs to events</li>
                <li>Photos and videos you uploaded</li>
                <li>Push notification tokens</li>
                <li>Account-level analytics identifiers</li>
                <li>Calendar entries created on your behalf within the app</li>
              </ul>
            </div>
          </section>

          <section id="in-app" className="da-section da-section--tight">
            <div className="wrap da-wrap">
              <span className="eyebrow">Option 1 — inside the app</span>
              <h2 className="da-h2">Delete from the Gather app</h2>
              <ol className="da-steps">
                <li>
                  <span className="da-step__num">1</span>
                  <span className="da-step__body">Open the Gather app on your device.</span>
                </li>
                <li>
                  <span className="da-step__num">2</span>
                  <span className="da-step__body">Go to <strong>Settings</strong>.</span>
                </li>
                <li>
                  <span className="da-step__num">3</span>
                  <span className="da-step__body">Tap <strong>Delete account</strong>.</span>
                </li>
                <li>
                  <span className="da-step__num">4</span>
                  <span className="da-step__body">Confirm in the dialog.</span>
                </li>
              </ol>
              <p className="da-note">
                Your account and personal data are removed immediately. Encrypted
                backups that may briefly still contain your data are rotated and
                permanently overwritten within 30 days.
              </p>
            </div>
          </section>

          <section className="da-section da-section--tight">
            <div className="wrap da-wrap">
              <span className="eyebrow">Option 2 — by email</span>
              <h2 className="da-h2">If you no longer have the app</h2>
              <p className="da-paragraph">
                Send an email to{' '}
                <a className="da-link" href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20Gather%20account`}>
                  {SUPPORT_EMAIL}
                </a>{' '}
                from the email address you used to register, with the subject line:
              </p>
              <pre className="da-code">Delete my Gather account</pre>
              <p className="da-paragraph">
                We will confirm receipt within 3 business days and complete the
                deletion within 30 days of the request.
              </p>
            </div>
          </section>

          <section className="da-section da-section--tight">
            <div className="wrap da-wrap">
              <span className="eyebrow">What we retain</span>
              <h2 className="da-h2">Limited data we keep</h2>
              <p className="da-paragraph">
                For fraud prevention, abuse moderation, and legal compliance, we
                may retain a small set of anonymized records for up to 12 months
                after account deletion:
              </p>
              <ul className="da-list">
                <li>Timestamps of moderation actions and reports</li>
                <li>Aggregated, non-identifying usage counts</li>
                <li>Transaction-level audit logs without personal identifiers</li>
              </ul>
              <p className="da-paragraph">
                These records contain no information that can be tied back to
                you personally.
              </p>
            </div>
          </section>

          <section className="da-section da-section--last">
            <div className="wrap da-wrap da-contact">
              <h2 className="da-h2">Questions?</h2>
              <p className="da-paragraph">
                Email us at{' '}
                <a className="da-link" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
                . We typically respond within 2 business days.
              </p>
              <p className="da-meta">Last updated: 21 May 2026</p>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </LocaleProvider>
  );
};
